<template>
  <div class="flex flex-col">
    <select-container
      :title="selectedDate ? DateTime.fromJSDate(selectedDate).toLocaleString(DateTime.DATE_MED) : 'בחירת תאריך ושעה'"
      :subtitle="summarySubtitle"
      icon="pi-calendar"
      @click="containerClick" />
    <span
      v-show="showError"
      :class="['text-xs text-red-500 mt-2 transition-opacity duration-200', showError ? 'opacity-100' : 'opacity-0']">יש לבחור שירותים כדי לצפות בתאריך זמינים</span>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, ref } from 'vue';
import { DateTime } from 'luxon';
import { IDropdownItem, IService } from '@torim-app/types';
import { SelectContainer, useDrawer } from '@torim-app/shared-components';
import SelectDateDrawerContent from './SelectDateDrawerContent.vue';

const props = defineProps({
  selectedDate: {
    type: Date,
    required: false
  },
  selectedHour: {
    type: Object as PropType<IDropdownItem>,
    required: false
  },
  preDate: {
    required: false,
    type: Date,
    default: undefined
  },
  selectedServices: {
    required: true,
    type: Array as PropType<IService[]>,
    default: () => []
  },
  selectedProvider: {
    required: true,
    type: String,
    default: ''
  },
  /** Owner-set visit length (minutes). Undefined => the services' own length. */
  selectedDuration: {
    required: false,
    type: Number,
    default: undefined
  }
});

const emit = defineEmits(['dateSelected', 'hourSelected', 'durationChanged', 'forcedHourChanged']);
const { openDrawer, closeDrawer } = useDrawer();

const showError = ref(false);

// Surface a shortened visit length on the closed row, so a non-default duration is
// visible without reopening the drawer.
const summarySubtitle = computed(() => {
  if (!props.selectedHour) return 'בחירת תאריר ושעת התחלה עבור התור';
  const time = props.selectedHour.name.replace('- admin', '').trim();
  return props.selectedDuration
    ? `בשעה ${time} · ${props.selectedDuration} דק׳`
    : `בשעה ${time}`;
});

const onDateSelected = (date: Date) => {
  emit('dateSelected', date);
};

const onHourSelected = (hour: IDropdownItem) => {
  emit('hourSelected', hour);
};

const onDurationChanged = (duration: number | undefined) => {
  emit('durationChanged', duration);
};

const onForcedHourChanged = (forced: boolean) => {
  emit('forcedHourChanged', forced);
};

const containerClick = () => {
  if (!props.selectedServices || props.selectedServices.length === 0 || !props.selectedProvider) {
    showError.value = true;
  } else {
    setTimeout(() => {
      showError.value = false;
      openDrawer(SelectDateDrawerContent, {
        selectedServices: props.selectedServices,
        selectedProvider: props.selectedProvider,
        preDate: props.preDate,
        initialDate: props.selectedDate,
        initialHour: props.selectedHour,
        initialDuration: props.selectedDuration,
        onDateSelected: onDateSelected,
        onHourSelected: onHourSelected,
        onDurationChanged: onDurationChanged,
        onForcedHourChanged: onForcedHourChanged,
        onCancel: () => {
          closeDrawer();
        }
      }, {
        title: 'בחירת תאריך',
        noPadding: true,
        full: true
      });
    }, 300);
  }
};
</script>
